import React, { useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Plane, Plus, Pencil, Trash2, Search, UserPlus } from 'lucide-react';
import type { AdminOutletContext, Flight } from '../types';
import { createFlight, deleteFlight as deleteFlightApi, fetchFlights, updateFlight } from '../lib/api';

const emptyForm: Omit<Flight, 'id'> = {
  airline: '',
  flightNumber: '',
  departure: '',
  arrival: '',
  departureTime: '09:00',
  arrivalTime: '12:00',
  price: 0,
  departureHour: 9,
  travelDate: '',
};

function parseHourFromTime(t: string): number {
  const h = parseInt(t.split(':')[0], 10);
  return Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 12;
}

const AdminFlights = () => {
  const { globalSearch } = useOutletContext<AdminOutletContext>();
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [localSearch, setLocalSearch] = useState('');
  const [editor, setEditor] = useState<{ mode: 'add' | 'edit'; flight: Flight } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const rows = await fetchFlights();
      setFlights(rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load flights');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const g = globalSearch.trim().toLowerCase();
    const l = localSearch.trim().toLowerCase();
    return flights.filter((f) => {
      const blob = `${f.airline} ${f.flightNumber} ${f.departure} ${f.arrival} ${f.travelDate ?? ''}`.toLowerCase();
      const okG = !g || blob.includes(g);
      const okL = !l || blob.includes(l);
      return okG && okL;
    });
  }, [flights, globalSearch, localSearch]);

  const openAdd = () => {
    setEditor({
      mode: 'add',
      flight: {
        id: `f_${Date.now()}`,
        ...emptyForm,
        travelDate: new Date().toISOString().split('T')[0],
      },
    });
  };

  const openEdit = (f: Flight) => setEditor({ mode: 'edit', flight: { ...f } });

  const deleteFlight = (id: string) => {
    if (!window.confirm('Remove this flight from inventory?')) return;
    void (async () => {
      try {
        await deleteFlightApi(id);
        setFlights((prev) => prev.filter((x) => x.id !== id));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete flight');
      }
    })();
  };

  const saveEditor = () => {
    if (!editor) return;
    const f = editor.flight;
    if (!f.airline.trim() || !f.flightNumber.trim() || !f.departure.trim() || !f.arrival.trim()) {
      window.alert('Fill airline, flight number, departure, and arrival.');
      return;
    }
    const normalized: Flight = {
      ...f,
      departureHour: parseHourFromTime(f.departureTime),
      price: Number(f.price) || 0,
    };
    void (async () => {
      try {
        if (editor.mode === 'add') {
          const { id: _, ...payload } = normalized;
          const created = await createFlight(payload);
          setFlights((prev) => [created, ...prev]);
        } else {
          const { id, ...patch } = normalized;
          const updated = await updateFlight(id, patch);
          setFlights((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        }
        setEditor(null);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save flight');
      }
    })();
  };

  const quoteWithFlight = (id: string) => {
    navigate(`/admin/agent/${agentId}/clients?flight=${encodeURIComponent(id)}`);
  };

  return (
    <div className="animate-fade">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: '800', letterSpacing: '-0.025em' }}>Flight inventory</h2>
          <p style={{ color: 'var(--text-muted)', maxWidth: '640px' }}>
            Start with an empty inventory and add flights step by step. These rows are used in quote search and can be assigned directly to clients.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={openAdd}
            style={{ padding: '0.65rem 1.25rem', borderRadius: '0.75rem', background: 'var(--accent)', color: 'white', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={20} /> Add flight
          </button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '1.5rem', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: '360px' }}>
            <Search size={18} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input value={localSearch} onChange={(e) => setLocalSearch(e.target.value)} placeholder="Filter inventory…" style={{ paddingLeft: '2.5rem', height: '40px', background: '#f8fafc' }} />
          </div>
          <button type="button" onClick={() => void load()} style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', background: '#f1f5f9' }}>
            Refresh
          </button>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{filtered.length} flights</span>
        </div>
        {error && <p style={{ color: '#dc2626', padding: '0.75rem 1.5rem 0' }}>{error}</p>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>Flight</th>
                <th style={{ padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>Route</th>
                <th style={{ padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>Date</th>
                <th style={{ padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>Times</th>
                <th style={{ padding: '0.85rem 1.25rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.7rem' }}>Base $</th>
                <th style={{ padding: '0.85rem 1.25rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.map((f) => (
                <tr key={f.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Plane size={16} color="var(--accent)" />
                      <span style={{ fontWeight: '700' }}>{f.airline}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{f.flightNumber}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.25rem', fontWeight: '600' }}>
                    {f.departure} → {f.arrival}
                  </td>
                  <td style={{ padding: '1rem 1.25rem', color: 'var(--text-muted)' }}>{f.travelDate ?? '—'}</td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    {f.departureTime} – {f.arrivalTime}
                  </td>
                  <td style={{ padding: '1rem 1.25rem', fontWeight: '800' }}>${f.price}</td>
                  <td style={{ padding: '1rem 1.25rem', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button type="button" title="Quote client" onClick={() => quoteWithFlight(f.id)} style={{ padding: '0.4rem 0.65rem', borderRadius: '0.5rem', background: 'var(--primary)', color: 'white', fontSize: '0.75rem', fontWeight: '700' }}>
                        <UserPlus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Quote
                      </button>
                      <button type="button" onClick={() => openEdit(f)} style={{ padding: '0.45rem', borderRadius: '0.5rem', background: '#f1f5f9' }}>
                        <Pencil size={16} />
                      </button>
                      <button type="button" onClick={() => deleteFlight(f.id)} style={{ padding: '0.45rem', borderRadius: '0.5rem', background: '#fef2f2', color: 'var(--danger)' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {loading && (
                <tr>
                  <td colSpan={6} style={{ padding: '2rem 1.25rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Loading flights...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '2rem 1.25rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No flights yet. Click <strong>Add flight</strong> to create your first entry.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '1.5rem' }}>
          <div className="animate-fade" style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', maxWidth: '640px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '1.35rem', fontWeight: '800', marginBottom: '1.25rem' }}>{editor.mode === 'add' ? 'Add flight' : 'Edit flight'}</h3>
            <p style={{ marginTop: '-0.6rem', marginBottom: '1.25rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Fill all core fields first (airline, flight no., route), then add schedule and price.
            </p>
            <div style={{ display: 'grid', gap: '1rem', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '1rem', padding: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Airline</label>
                  <input value={editor.flight.airline} placeholder="e.g. Lufthansa" onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, airline: e.target.value } })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Flight #</label>
                  <input value={editor.flight.flightNumber} placeholder="e.g. LH401" onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, flightNumber: e.target.value } })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Departure</label>
                  <input value={editor.flight.departure} placeholder="e.g. LHR" onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, departure: e.target.value } })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Arrival</label>
                  <input value={editor.flight.arrival} placeholder="e.g. JFK" onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, arrival: e.target.value } })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Dep time</label>
                  <input type="time" value={editor.flight.departureTime} onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, departureTime: e.target.value } })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Arr time</label>
                  <input type="time" value={editor.flight.arrivalTime} onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, arrivalTime: e.target.value } })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Travel date</label>
                  <input type="date" value={editor.flight.travelDate ?? ''} onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, travelDate: e.target.value } })} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: '800', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>Base price (USD)</label>
                <input type="number" min={0} value={editor.flight.price || ''} onChange={(e) => setEditor({ ...editor, flight: { ...editor.flight, price: Number(e.target.value) } })} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button type="button" onClick={() => setEditor(null)} style={{ padding: '0.65rem 1.25rem', borderRadius: '0.75rem', background: '#f1f5f9', fontWeight: '600' }}>
                Cancel
              </button>
              <button type="button" onClick={saveEditor} style={{ padding: '0.65rem 1.5rem', borderRadius: '0.75rem', background: 'var(--primary)', color: 'white', fontWeight: '700' }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFlights;
